import type {Agency} from 'src/types/agency';

import type {AuthenticationActions} from 'src/action-creators/authentication';
import type {
  CredsAuthentication,
  ApiErrorWithStatusCode,
} from 'src/types/authentication';

import {
  CREDS_AUTHENTICATION,
  CREDS_AUTHENTICATION_ERROR,
  CREDS_AUTHENTICATED,
  NEW_PHONE_REGISTRATION,
  OTP_VERIFICATION_ERROR,
  NEW_PHONE_REGISTRATION_ERROR,
  RESEND_OTP_ERROR,
  RESET_MFA_DEVICE_ERROR,
  RESET_PHONE_APPROVE_ERROR,
  RESET_PHONE_APPROVED,
  RESET_TOTP_APPROVE_ERROR,
  RESET_TOTP_APPROVED,
  CHANNEL_SELECTION_TOTP_ERROR,
  VALID_AGENCIES,
  VALID_AGENCIES_ERROR,
  FORGOT_PASSWORD_ERROR,
  RESET_PASSWORD_ERROR,
  SIGNUP_ERROR,
} from 'src/action-creators/authentication';


export const AuthenticationStatus = {
  PENDING: 'pending',
  AUTHENTICATING: 'authenticating',
  AUTHENTICATED: 'authenticated',
  ERROR: 'error',
} as const;

export type AuthenticationState = {
  loginId: string | null | undefined;
  credAuthenticationStatus: string;
  authenticationData: CredsAuthentication | null | undefined;
  newPhone: string | null | undefined;
  signInError: string | null | undefined;
  retryInMins: number | null | undefined;
  newPhoneRegistrationError: string | null | undefined;
  otpVerificationError: ApiErrorWithStatusCode | null | undefined;
  resendOTPError: string | null | undefined;
  resetMfaDeviceError: string | null | undefined;
  resetPhoneApproveError: string | null | undefined;
  resetPhoneApproved: string | null | undefined;
  validAgencies: Agency[] | null | undefined;
  forgotPasswordError: string | null | undefined;
  resetPasswordError: string | null | undefined;
  signUpError: string | null | undefined;
  otpLimitExceeded: boolean | null | undefined;
  resetTotpApproveError: string | null | undefined;
  resetTotpApproved: string | null | undefined;
  channelSelectionTotpError: string | null | undefined;
};

const initialState = {
  loginId: null,
  credAuthenticationStatus: AuthenticationStatus.PENDING,
  authenticationData: null,
  newPhone: null,
  signInError: null,
  retryInMins: 0,
  otpVerificationError: null,
  resendOTPError: null,
  resetMfaDeviceError: null,
  resetPhoneApproveError: null,
  resetPhoneApproved: null,
  newPhoneRegistrationError: null,
  validAgencies: null,
  forgotPasswordError: null,
  resetPasswordError: null,
  signUpError: null,
  otpLimitExceeded: false,
  resetTotpApproveError: '',
  resetTotpApproved: '',
  channelSelectionTotpError: '',
} as const;

export default (
  state: AuthenticationState | null | undefined = initialState,
  action: AuthenticationActions,
): AuthenticationState => {
  switch (action.type) {
    case CREDS_AUTHENTICATION:
      // @ts-expect-error - TS2322 - Type '{ credAuthenticationStatus: "authenticating"; authenticationData: null; loginId?: string | null | undefined; newPhone?: string | null | undefined; signInError?: string | null | undefined; ... 14 more ...; channelSelectionTotpError?: string | null | undefined; }' is not assignable to type 'AuthenticationState'.
      return {
        ...state,
        credAuthenticationStatus: AuthenticationStatus.AUTHENTICATING,
        authenticationData: null,
      };
    case CREDS_AUTHENTICATION_ERROR:
      // @ts-expect-error - TS2322 - Type '{ credAuthenticationStatus: "error"; authenticationData: null; signInError: string; retryInMins: number; loginId?: string | null | undefined; newPhone?: string | null | undefined; newPhoneRegistrationError?: string | null | undefined; ... 12 more ...; channelSelectionTotpError?: string | null | undefined; }' is not assignable to type 'AuthenticationState'.
      return {
        ...state,
        credAuthenticationStatus: AuthenticationStatus.ERROR,
        authenticationData: null,
        signInError: action.payload.error,
        retryInMins: action.payload.retryLoginInMins,
      };
    case CREDS_AUTHENTICATED:
      // @ts-expect-error - TS2322 - Type '{ credAuthenticationStatus: "authenticated"; authenticationData: CredsAuthentication; signInError: null; otpVerificationError: null; resendOTPError: null; resetMfaDeviceError: null; ... 13 more ...; channelSelectionTotpError?: string | null | undefined; }' is not assignable to type 'AuthenticationState'.
      return {
        ...state,
        credAuthenticationStatus: AuthenticationStatus.AUTHENTICATED,
        authenticationData: action.payload,
        signInError: null,
        otpVerificationError: null,
        resendOTPError: null,
        resetMfaDeviceError: null,
        resetPhoneApproveError: null,
        newPhoneRegistrationError: null,
        forgotPasswordError: null,
        resetPasswordError: null,
        signUpError: null,
      };
    case NEW_PHONE_REGISTRATION:
      // @ts-expect-error - TS2322 - Type '{ newPhone: string; loginId?: string | null | undefined; credAuthenticationStatus?: string | undefined; authenticationData?: CredsAuthentication | null | undefined; signInError?: string | null | undefined; ... 14 more ...; channelSelectionTotpError?: string | null | undefined; }' is not assignable to type 'AuthenticationState'.
      return {
        ...state,
        newPhone: action.payload,
      };
    case OTP_VERIFICATION_ERROR:
      // @ts-expect-error - TS2322 - Type '{ otpVerificationError: ApiErrorWithStatusCode; otpLimitExceeded: boolean; loginId?: string | null | undefined; credAuthenticationStatus?: string | undefined; ... 15 more ...; channelSelectionTotpError?: string | null | undefined; }' is not assignable to type 'AuthenticationState'.
      return {
        ...state,
        otpVerificationError: action.payload,
        otpLimitExceeded:
          action.payload.status_code === 429 ||
          action.payload.status_code === 403,
      };
    case NEW_PHONE_REGISTRATION_ERROR:
      // @ts-expect-error - TS2322 - Type '{ newPhoneRegistrationError: string; loginId?: string | null | undefined; credAuthenticationStatus?: string | undefined; authenticationData?: CredsAuthentication | null | undefined; ... 15 more ...; channelSelectionTotpError?: string | null | undefined; }' is not assignable to type 'AuthenticationState'.
      return {
        ...state,
        newPhoneRegistrationError: action.payload,
      };
    case RESEND_OTP_ERROR:
      // @ts-expect-error - TS2322 - Type '{ resendOTPError: string; otpLimitExceeded: boolean; loginId?: string | null | undefined; credAuthenticationStatus?: string | undefined; authenticationData?: CredsAuthentication | null | undefined; ... 14 more ...; channelSelectionTotpError?: string | null | undefined; }' is not assignable to type 'AuthenticationState'.
      return {
        ...state,
        resendOTPError: action.payload.error,
        otpLimitExceeded:
          action.payload.status_code === 429 ||
          action.payload.status_code === 403,
      };
    case RESET_MFA_DEVICE_ERROR:
      // @ts-expect-error - TS2322 - Type '{ resetMfaDeviceError: string; loginId?: string | null | undefined; credAuthenticationStatus?: string | undefined; authenticationData?: CredsAuthentication | null | undefined; ... 15 more ...; channelSelectionTotpError?: string | null | undefined; }' is not assignable to type 'AuthenticationState'.
      return {
        ...state,
        resetMfaDeviceError: action.payload,
      };
    case RESET_PHONE_APPROVE_ERROR:
      // @ts-expect-error - TS2322 - Type '{ resetPhoneApproveError: string; loginId?: string | null | undefined; credAuthenticationStatus?: string | undefined; authenticationData?: CredsAuthentication | null | undefined; ... 15 more ...; channelSelectionTotpError?: string | null | undefined; }' is not assignable to type 'AuthenticationState'.
      return {
        ...state,
        resetPhoneApproveError: action.payload,
      };
    case RESET_PHONE_APPROVED:
      // @ts-expect-error - TS2322 - Type '{ resetPhoneApproved: string; loginId?: string | null | undefined; credAuthenticationStatus?: string | undefined; authenticationData?: CredsAuthentication | null | undefined; ... 15 more ...; channelSelectionTotpError?: string | null | undefined; }' is not assignable to type 'AuthenticationState'.
      return {
        ...state,
        resetPhoneApproved: action.payload,
      };
    case RESET_TOTP_APPROVE_ERROR:
      // @ts-expect-error - TS2322 - Type '{ resetTotpApproveError: string; loginId?: string | null | undefined; credAuthenticationStatus?: string | undefined; authenticationData?: CredsAuthentication | null | undefined; ... 15 more ...; channelSelectionTotpError?: string | null | undefined; }' is not assignable to type 'AuthenticationState'.
      return {
        ...state,
        resetTotpApproveError: action.payload,
      };
    case RESET_TOTP_APPROVED:
      // @ts-expect-error - TS2322 - Type '{ resetTotpApproved: string; loginId?: string | null | undefined; credAuthenticationStatus?: string | undefined; authenticationData?: CredsAuthentication | null | undefined; newPhone?: string | null | undefined; ... 14 more ...; channelSelectionTotpError?: string | null | undefined; }' is not assignable to type 'AuthenticationState'.
      return {
        ...state,
        resetTotpApproved: action.payload,
      };
    case CHANNEL_SELECTION_TOTP_ERROR:
      // @ts-expect-error - TS2322 - Type '{ channelSelectionTotpError: string; loginId?: string | null | undefined; credAuthenticationStatus?: string | undefined; authenticationData?: CredsAuthentication | null | undefined; ... 15 more ...; resetTotpApproved?: string | null | undefined; }' is not assignable to type 'AuthenticationState'.
      return {
        ...state,
        channelSelectionTotpError: action.payload,
      };
    case VALID_AGENCIES:
      // @ts-expect-error - TS2322 - Type '{ credAuthenticationStatus: "authenticated"; signInError: null; validAgencies: Agency[]; loginId?: string | null | undefined; authenticationData?: CredsAuthentication | null | undefined; ... 14 more ...; channelSelectionTotpError?: string | null | undefined; }' is not assignable to type 'AuthenticationState'.
      return {
        ...state,
        credAuthenticationStatus: AuthenticationStatus.AUTHENTICATED,
        signInError: null,
        validAgencies: action.payload,
      };
    case VALID_AGENCIES_ERROR:
      // @ts-expect-error - TS2322 - Type '{ signInError: string; credAuthenticationStatus: "error"; loginId?: string | null | undefined; authenticationData?: CredsAuthentication | null | undefined; newPhone?: string | null | undefined; ... 14 more ...; channelSelectionTotpError?: string | null | undefined; }' is not assignable to type 'AuthenticationState'.
      return {
        ...state,
        signInError: action.payload,
        credAuthenticationStatus: AuthenticationStatus.ERROR,
      };
    case FORGOT_PASSWORD_ERROR:
      // @ts-expect-error - TS2322 - Type '{ forgotPasswordError: string; loginId?: string | null | undefined; credAuthenticationStatus?: string | undefined; authenticationData?: CredsAuthentication | null | undefined; ... 15 more ...; channelSelectionTotpError?: string | null | undefined; }' is not assignable to type 'AuthenticationState'.
      return {
        ...state,
        forgotPasswordError: action.payload,
      };
    case RESET_PASSWORD_ERROR:
      // @ts-expect-error - TS2322 - Type '{ resetPasswordError: string; loginId?: string | null | undefined; credAuthenticationStatus?: string | undefined; authenticationData?: CredsAuthentication | null | undefined; ... 15 more ...; channelSelectionTotpError?: string | null | undefined; }' is not assignable to type 'AuthenticationState'.
      return {
        ...state,
        resetPasswordError: action.payload,
      };
    case SIGNUP_ERROR:
      // @ts-expect-error - TS2322 - Type '{ signUpError: string; loginId?: string | null | undefined; credAuthenticationStatus?: string | undefined; authenticationData?: CredsAuthentication | null | undefined; newPhone?: string | null | undefined; ... 14 more ...; channelSelectionTotpError?: string | null | undefined; }' is not assignable to type 'AuthenticationState'.
      return {
        ...state,
        signUpError: action.payload,
      };
    default:
      // @ts-expect-error - TS2322 - Type 'AuthenticationState | null' is not assignable to type 'AuthenticationState'.
      return state;
  }
};
